/**
 * The four tabs of the design's Settings that belong to one person rather than
 * to the studio: notifications, preferences, permissions and data.
 *
 * Every switch here reaches the server (`PATCH /me { preferences }`) and does
 * something on the next render. Where the design had a switch for something
 * the platform does not do yet — email, push, a weekly digest — the row says
 * so instead of offering a toggle that would teach people it is on.
 */

import type { ReactNode } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Button } from '../../components/Button'
import { Toggle } from '../../components/Toggle'
import type { Preferences } from '../../api'
import { ROLE_LABEL, useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppDataContext'
import { useToast } from '../../context/ToastContext'
import { apiErrorMessage } from '../../lib/api'
import { downloadCsv } from '../../lib/csv'
import { today } from '../../lib/format'
import { Select } from '../../components/Select'

const selectClass =
  'h-9 min-w-[10rem] rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text-active transition-colors hover:border-shell-text focus:border-brand-400'

function Row({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-shell-text-active">{title}</p>
        <p className="mt-0.5 text-xs text-shell-text">{description}</p>
      </div>
      {children}
    </div>
  )
}

/** Save a change to one's own preferences, with a toast either way. */
function useSave() {
  const { savePreferences } = useAuth()
  const { showToast } = useToast()
  const [saving, setSaving] = useState(false)
  async function save(change: Preferences, said: string) {
    setSaving(true)
    try {
      await savePreferences(change)
      showToast(said)
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setSaving(false)
    }
  }
  return { save, saving }
}

const IN_APP: { key: 'overdue' | 'feedback' | 'waiting'; title: string; description: string }[] = [
  { key: 'overdue', title: 'Overdue work', description: 'A piece past its due date and not yet delivered.' },
  { key: 'feedback', title: 'A client asks for a change', description: 'Feedback on a piece that needs somebody to act on it.' },
  { key: 'waiting', title: 'Waiting on a client', description: 'A piece sent for approval that the client has not answered.' }
]

const NOT_SENT: { title: string; description: string }[] = [
  { title: 'Email', description: 'The platform does not send email yet.' },
  { title: 'Push to this phone', description: 'The platform does not send push notifications yet.' },
  { title: 'Weekly digest', description: 'Not sent yet. The Today page is the daily version of it.' }
]

export function NotificationsTab() {
  const { currentUser } = useAuth()
  const { save, saving } = useSave()
  const wants = currentUser?.preferences.notifications ?? {}

  return (
    <div className="flex flex-col gap-6">
      <section>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">In the bell</p>
        <div className="divide-y divide-shell-border">
          {IN_APP.map((n) => {
            const on = wants[n.key] !== false
            return (
              <Row key={n.key} title={n.title} description={n.description}>
                <Toggle
                  label={n.title}
                  checked={on}
                  onChange={(v) => {
                    if (saving) return
                    void save({ notifications: { [n.key]: v } }, `${n.title}: ${v ? 'on' : 'off'}.`)
                  }}
                />
              </Row>
            )
          })}
        </div>
      </section>

      <section className="border-t border-ink-100 pt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Elsewhere</p>
        <div className="divide-y divide-shell-border">
          {NOT_SENT.map((n) => (
            <Row key={n.title} title={n.title} description={n.description}>
              <span className="shrink-0 rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-500">
                Not sent
              </span>
            </Row>
          ))}
        </div>
      </section>
    </div>
  )
}

const LANDING: { value: NonNullable<Preferences['landingPage']>; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'board', label: 'Board' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'content', label: 'Content plan' },
  { value: 'calendar', label: 'Calendar' }
]

const DATE_FORMATS: NonNullable<Preferences['dateFormat']>[] = ['D MMM YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']

function sample(format: string): string {
  const [y, m, d] = today().split('-')
  if (format === 'DD/MM/YYYY') return `${d}/${m}/${y}`
  if (format === 'MM/DD/YYYY') return `${m}/${d}/${y}`
  if (format === 'YYYY-MM-DD') return `${y}-${m}-${d}`
  return new Date(`${today()}T00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

export function PreferencesTab() {
  const { currentUser } = useAuth()
  const { save, saving } = useSave()
  const prefs = currentUser?.preferences ?? {}

  return (
    <div className="divide-y divide-shell-border">
      <Row title="Open the app on" description="The page you land on after signing in.">
        <Select
          aria-label="Landing page"
          className={selectClass}
          disabled={saving}
          value={prefs.landingPage ?? 'today'}
          onChange={(e) => {
            const v = e.target.value as NonNullable<Preferences['landingPage']>
            void save({ landingPage: v }, `The app now opens on ${LANDING.find((l) => l.value === v)?.label ?? v}.`)
          }}
        >
          {LANDING.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </Select>
      </Row>
      <Row title="Full dates read as" description="Anywhere a date is written with its year. Short dates stay “8 Sep”.">
        <Select
          aria-label="Date format"
          className={selectClass}
          disabled={saving}
          value={prefs.dateFormat ?? 'D MMM YYYY'}
          onChange={(e) => {
            const v = e.target.value as NonNullable<Preferences['dateFormat']>
            void save({ dateFormat: v }, `Dates now read as ${sample(v)}.`)
          }}
        >
          {DATE_FORMATS.map((f) => (
            <option key={f} value={f}>
              {sample(f)}
            </option>
          ))}
        </Select>
      </Row>
      <Row title="Assign new tasks to me" description="A task you create starts with you as its owner. You can still change it.">
        <Toggle
          label="Assign new tasks to me"
          checked={prefs.autoAssign !== false}
          onChange={(v) => {
            if (saving) return
            void save({ autoAssign: v }, v ? 'New tasks will be yours.' : 'New tasks start unassigned.')
          }}
        />
      </Row>
    </div>
  )
}

/** What an operation key reads as to a person: "tasks.create" → "Tasks: create". */
function readable(key: string): string {
  const [area, ...rest] = key.split('.')
  const what = rest.join(' ').replace(/_/g, ' ')
  const label = (area ?? key).replace(/_/g, ' ')
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}${what === '' ? '' : `: ${what}`}`
}

const REACH: Record<string, string> = {
  PARTNER: 'Everything, including money, the roster and the studio’s settings.',
  MANAGER: 'Every client and all the work, but not money, the roster or the studio’s settings.',
  TEAM: 'The clients you are assigned to, and the work on them. Nothing is deleted from this role.',
  VIEWER: 'Reads the clients you are assigned to. Changes nothing.'
}

export function PermissionsTab() {
  const { currentUser, hasRole } = useAuth()
  if (currentUser === null) return null
  const denied = [...currentUser.denied].sort()

  return (
    <div className="flex flex-col gap-6">
      <section>
        <p className="text-sm font-medium text-shell-text-active">Your role</p>
        <p className="mt-1 text-lg font-semibold text-shell-text-active">{ROLE_LABEL[currentUser.role]}</p>
        <p className="mt-1 text-xs text-ink-500">{REACH[currentUser.role]}</p>
      </section>

      <section className="border-t border-ink-100 pt-6">
        <p className="text-sm font-medium text-shell-text-active">Switched off for your role</p>
        {denied.length === 0 ? (
          <p className="mt-1 text-xs text-ink-500">Nothing. You have everything your role allows.</p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {denied.map((d) => (
              <li key={d} className="rounded-full bg-ink-100 px-2.5 py-1 text-xs text-ink-600">
                {readable(d)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="border-t border-ink-100 pt-6">
        {hasRole('PARTNER') ? (
          <Link to="/admin/permissions" className="text-sm font-medium text-brand-600 hover:underline">
            Change what each role can do →
          </Link>
        ) : (
          <p className="text-xs text-ink-500">An Admin sets what each role can do, under Admin → Permissions.</p>
        )}
      </section>
    </div>
  )
}

export function DataTab() {
  const { hasPermission } = useAuth()
  const { clients, tasks, content, directory } = useAppData()
  const { showToast } = useToast()
  const stamp = today()

  const clientName = new Map(clients.map((c) => [c.id, c.name]))
  const personName = new Map(directory.map((p) => [p.id, p.name]))

  const exports: { key: string; resource: string; title: string; description: string; run: () => number }[] = [
    {
      key: 'clients',
      resource: 'clients',
      title: 'Clients',
      description: `${clients.length} clients, with their contact, status and who works on them.`,
      run: () => {
        const rows = clients.map((c) => ({
          name: c.name,
          industry: c.industry,
          status: c.accountStatus,
          contact: c.contactName,
          email: c.contactEmail,
          team: c.assignedTeamIds.map((id) => personName.get(id) ?? id).join('; '),
          ...(c.monthlyRetainer === undefined ? {} : { retainer: c.monthlyRetainer, currency: c.retainerCurrency ?? '' })
        }))
        downloadCsv(`clients-${stamp}.csv`, rows)
        return rows.length
      }
    },
    {
      key: 'tasks',
      resource: 'tasks',
      title: 'Tasks',
      description: `${tasks.length} tasks, with client, owner, due date and stage.`,
      run: () => {
        const rows = tasks.map((t) => ({
          title: t.title,
          client: clientName.get(t.clientId) ?? t.clientId,
          owner: personName.get(t.assigneeId) ?? t.assigneeId,
          due: t.dueDate,
          stage: t.status,
          priority: t.priority,
          tags: t.tags.join('; ')
        }))
        downloadCsv(`tasks-${stamp}.csv`, rows)
        return rows.length
      }
    },
    {
      key: 'content',
      resource: 'content',
      title: 'Content plan',
      description: `${content.length} pieces in the month the content plan is on, with kind, platform, hook, caption and links.`,
      run: () => {
        const rows = content.map((c) => ({
          date: c.date,
          client: clientName.get(c.clientId) ?? c.clientId,
          title: c.title,
          kind: c.contentType,
          platform: c.platform,
          stage: c.status,
          owner: personName.get(c.assignedToId) ?? c.assignedToId,
          design: c.designStatus,
          editing: c.editingStatus,
          approval: c.approvalStatus,
          posting: c.postingStatus,
          posted: c.postedDate ?? '',
          topic: c.topic,
          hook: c.hook,
          caption: c.caption,
          cta: c.cta,
          link: c.reelLink,
          notes: c.notes
        }))
        downloadCsv(`content-plan-${stamp}.csv`, rows)
        return rows.length
      }
    }
  ]

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-500">
        A spreadsheet of what you can see, as of now. Money is only in it for an Admin.
      </p>
      <div className="divide-y divide-shell-border">
        {exports.map((x) => {
          const allowed = hasPermission(x.resource, 'export')
          return (
            <Row key={x.key} title={x.title} description={allowed ? x.description : 'An Admin has switched off exporting this for your role.'}>
              <Button
                variant="secondary"
                disabled={!allowed}
                onClick={() => {
                  const n = x.run()
                  showToast(n === 0 ? 'Nothing to export.' : `${n} rows downloaded.`, n === 0 ? 'info' : 'success')
                }}
              >
                <Download size={14} />
                CSV
              </Button>
            </Row>
          )
        })}
      </div>
    </div>
  )
}
